/**
 * Region de anuncios para lectores de pantalla.
 *
 * En esta aplicacion pasan cosas sin que el usuario toque nada: se llega a un
 * punto, se desbloquea una pregunta, cambia el estado de la ubicacion. Quien no
 * ve la pantalla necesita que eso se anuncie, o el juego es literalmente
 * inutilizable.
 *
 * Se usa role="status" (cortesia) y no "alert": interrumpir la lectura en curso
 * cada vez que cambia una distancia seria insoportable.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface ContextoAnuncios {
  anunciar: (mensaje: string) => void;
}

const Contexto = createContext<ContextoAnuncios | null>(null);

export function ProveedorAnuncios({ children }: { children: ReactNode }) {
  const [mensaje, setMensaje] = useState('');

  const anunciar = useCallback((texto: string) => {
    // Se limpia primero para que un mensaje repetido vuelva a leerse: sin esto,
    // llegar dos veces al mismo aviso pasaria en silencio.
    setMensaje('');
    window.setTimeout(() => {
      setMensaje(texto);
    }, 60);
  }, []);

  const valor = useMemo(() => ({ anunciar }), [anunciar]);

  return (
    <Contexto.Provider value={valor}>
      {children}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {mensaje}
      </div>
    </Contexto.Provider>
  );
}

export function useAnuncios(): ContextoAnuncios {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useAnuncios necesita estar dentro de ProveedorAnuncios');
  return contexto;
}
